// components/InvoiceDesigner/BillDesignRenderer.jsx
import React from 'react';

const BillDesignRenderer = ({ design, dynamicData }) => { // Removed 'content' from parameters
    if (!design) {
        console.error("Design is undefined!");
        return <div>No design found</div>;
    }
    
    const { elements, backgroundColor, canvasSize } = design;
    
    if (!dynamicData) {
        console.error("dynamicData is undefined!");
        return <div>Loading data...</div>;
    }

    console.log("Dynamic Data in BillDesignRenderer:", dynamicData);
    console.log("Design elements:", elements);
    
    // Function to replace placeholders with actual data
    const parseContent = (content) => {
        if (!content || typeof content !== 'string') return content;
        
        const replacements = {
            '{{invoiceNumber}}': dynamicData.invoiceNumber || '',
            '{{customerName}}': dynamicData.customerName || '',
            '{{tableNumber}}': dynamicData.tableNumber || '',
            '{{date}}': dynamicData.date || '',
            '{{time}}': dynamicData.time || '',
            '{{cashier}}': dynamicData.cashier || '',
            '{{waiter}}': dynamicData.waiter || '',
            '{{subtotal}}': dynamicData.subtotal ? `₹ ${dynamicData.subtotal}` : '₹ 0',
            '{{cgst}}': dynamicData.gst?.cgst ? `₹ ${dynamicData.gst.cgst}` : '₹ 0',
            '{{sgst}}': dynamicData.gst?.sgst ? `₹ ${dynamicData.gst.sgst}` : '₹ 0',
            '{{grandTotal}}': dynamicData.grandTotal ? `₹ ${dynamicData.grandTotal}` : '₹ 0',
            '{{companyName}}': dynamicData.companyInfo?.name || '',
            '{{companyAddress}}': dynamicData.companyInfo?.address || '',
            '{{companyPhone}}': dynamicData.companyInfo?.phone || '',
            '{{companyGST}}': dynamicData.companyInfo?.gst || '',
            '{{companyLogo}}': dynamicData.companyInfo?.logo || '',
        };
        
        let parsedContent = content;
        Object.entries(replacements).forEach(([placeholder, value]) => {
            parsedContent = parsedContent.replace(new RegExp(placeholder, 'g'), value);
        });
        
        return parsedContent;
    };

    // Handle dynamic elements like item lists
    const renderElement = (element) => {
        const { type, content, x, y, width, height, styles = {} } = element;
        
        const baseStyle = {
            position: 'absolute',
            left: `${x}px`,
            top: `${y}px`,
            width: `${width}px`,
            height: `${height}px`,
            ...styles,
            fontSize: styles.fontSize || '12px',
            fontFamily: styles.fontFamily || 'Arial, sans-serif',
            color: styles.color || '#000000',
            textAlign: styles.textAlign || 'left',
            backgroundColor: styles.backgroundColor || 'transparent',
        };

        switch (type) {
            case 'text':
            case 'header':
            case 'subheader':
            case 'price':
            case 'invoice_number':
            case 'table_number':
            case 'customer_name': // Added this case
            case 'date_time':
            case 'cashier':
            case 'waiter':
            case 'subtotal':
            case 'cgst':
            case 'sgst':
            case 'total':
                return (
                    <div 
                        style={baseStyle} 
                        className={`whitespace-pre-wrap break-words ${type === 'header' 
                            || type === 'subheader' || type === 'price' ? 'font-bold' : ''}`}
                    >
                        {parseContent(content)}
                    </div>
                );
            
            case 'image':
                const imageSrc = content === '{{companyLogo}}' 
                    ? dynamicData.companyInfo?.logo 
                    : content;
                
                console.log("Image content:", content, "Image src:", imageSrc);
                
                return (
                    <div style={baseStyle}>
                        {imageSrc ? (
                            <img 
                                src={imageSrc} 
                                alt="" 
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: styles.objectFit || 'contain',
                                    borderRadius: styles.borderRadius || '0px',
                                    border: styles.border || 'none',
                                }}
                                onError={(e) => {
                                    console.error("Image failed to load:", imageSrc);
                                    e.target.src = "https://via.placeholder.com/150x150?text=Logo";
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#9ca3af',
                                fontSize: '10px'
                            }}>
                                [Image]
                            </div>
                        )}
                    </div>
                );
            
            case 'line':
                return (
                    <div 
                        style={{
                            ...baseStyle,
                            borderTop: styles.borderTop || '1px solid #000',
                            borderBottom: 'none',
                            borderLeft: 'none',
                            borderRight: 'none',
                        }}
                    />
                );
            
            case 'items_table':
                return renderItemsTable(element, dynamicData);
            
            case 'gst_summary':
                return renderGSTSummary(element, dynamicData);
            
            default:
                console.warn(`Unknown element type: ${type}`);
                return (
                    <div style={baseStyle}>
                        {parseContent(content)}
                    </div>
                );
        }
    };

    // Render dynamic items table
    const renderItemsTable = (element, data) => {
        const { x, y, width, height, styles = {} } = element;
        const allItems = [...(data.items || []), ...(data.addons || [])];
        
        const itemHeight = 20;
        const headerHeight = 25;
        const neededHeight = allItems.length * itemHeight + headerHeight + 10;
        
        return (
            <div style={{
                position: 'absolute',
                left: `${x}px`,
                top: `${y}px`,
                width: `${width}px`,
                height: `${Math.max(height, neededHeight)}px`,
                ...styles,
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}>
                {/* Table header */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #000',
                    paddingBottom: '4px',
                    marginBottom: '4px',
                    fontWeight: 'bold',
                    fontSize: styles.fontSize || '10px',
                    height: `${headerHeight}px`,
                }}>
                    <div style={{ flex: 3, textAlign: 'left' }}>Item</div>
                    <div style={{ flex: 1, textAlign: 'right', paddingRight: '10px' }}>Price</div>
                    <div style={{ flex: 1, textAlign: 'right', paddingRight: '10px' }}>Qty</div>
                    <div style={{ flex: 1, textAlign: 'right' }}>Total</div>
                </div>
                
                {/* Table rows */}
                <div style={{ maxHeight: `${height - headerHeight - 10}px`, overflowY: 'auto' }}>
                    {allItems.map((item, index) => (
                        <div 
                            key={index}
                            style={{
                                display: 'flex',
                                padding: '2px 0',
                                fontSize: styles.itemFontSize || '9px',
                                borderBottom: '0.5px dashed #ccc',
                                height: `${itemHeight}px`,
                                alignItems: 'center',
                            }}
                        >
                            <div style={{ flex: 3, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.name}
                            </div>
                            <div style={{ flex: 1, textAlign: 'right', paddingRight: '10px' }}>₹ {item.rate || item.price || 0}</div>
                            <div style={{ flex: 1, textAlign: 'right', paddingRight: '10px' }}>{item.quantity || 1}</div>
                            <div style={{ flex: 1, textAlign: 'right' }}>₹ {item.total || 0}</div>
                        </div>
                    ))}
                    
                    {allItems.length === 0 && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '50px',
                            color: '#9ca3af',
                            fontSize: '10px'
                        }}>
                            No items
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render GST summary
    const renderGSTSummary = (element, data) => {
        const { x, y, width, styles = {} } = element;
        
        return (
            <div style={{
                position: 'absolute',
                left: `${x}px`,
                top: `${y}px`,
                width: `${width}px`,
                ...styles,
                boxSizing: 'border-box',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '10px' }}>
                    <span>Sub-Total:</span>
                    <span>₹ {data.subtotal || 0}</span>
                </div>
                {(data.billAdjustments?.rows || []).map((row, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '10px' }}>
                        <span>{row.label}:</span>
                        <span>₹ {row.amount || 0}</span>
                    </div>
                ))}
                {parseFloat(data.billAdjustments?.discountAmount || 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '10px' }}>
                        <span>Discount:</span>
                        <span>-₹ {data.billAdjustments.discountAmount}</span>
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '10px' }}>
                    <span>CGST ({data.gst?.rate || 5}%):</span>
                    <span>₹ {data.gst?.cgst || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '10px' }}>
                    <span>SGST ({data.gst?.rate || 5}%):</span>
                    <span>₹ {data.gst?.sgst || 0}</span>
                </div>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    fontWeight: 'bold', 
                    marginTop: '8px', 
                    borderTop: '1px dashed #000', 
                    paddingTop: '6px',
                    fontSize: '11px'
                }}>
                    <span>Grand Total:</span>
                    <span>₹ {data.grandTotal || 0}</span>
                </div>
            </div>
        );
    };

    return (
        <div 
            style={{
                width: `${canvasSize?.width || 192}px`,
                minHeight: `${canvasSize?.height || 400}px`,
                backgroundColor: backgroundColor || '#ffffff',
                position: 'relative',
                margin: '0 auto',
                boxSizing: 'border-box',
                padding: '10px',
                fontFamily: 'Arial, sans-serif',
            }}
            className="bill-renderer"
        >
            {elements?.map((element, index) => (
                <React.Fragment key={element.id || index}>
                    {renderElement(element)}
                </React.Fragment>
            ))}
        </div>
    );
};

export default BillDesignRenderer;
